import { rankMockRecommendations } from "./buyerRecommendationMatching";
import { buildRecommendationWhatsappHref } from "./buyerRecommendationService";
import { trackQuizEvent as trackCentralQuizEvent } from "../../utils/buyerAnalytics";
import {
  BUDGET_OPTION_DEFS,
  FINANCING_LOAN_OPTION_VALUES,
  FINANCING_OPTION_VALUES,
  FUEL_OPTION_VALUES,
  LOAN_TERM_OPTION_VALUES,
  QUIZ_SOURCE,
  QUIZ_STORAGE_KEY,
  TIMELINE_OPTION_VALUES,
  TRANSMISSION_OPTION_VALUES,
  USE_CASE_OPTION_VALUES,
  VEHICLE_TYPE_OPTION_VALUES,
} from "./buyerConciergeQuizConstants";

export function resolveIntentCities(intentOrState) {
  if (intentOrState?.cities?.length) return intentOrState.cities;
  if (intentOrState?.city) return [intentOrState.city];
  return [];
}

export function createEmptyQuizState() {
  return {
    budgetRange: null,
    customMaxBudget: "",
    cities: [],
    neighborhood: "",
    vehicleType: null,
    useCases: [],
    transmission: null,
    fuel: null,
    financingPreference: null,
    downPayment: "",
    loanTermMonths: null,
    purchaseTimeline: null,
    name: "",
    whatsapp: "",
    email: "",
    consentToShare: false,
  };
}

export function intentToFormState(intent) {
  if (!intent) return createEmptyQuizState();
  return {
    budgetRange: intent.budgetRange,
    customMaxBudget: intent.customMaxBudget ? String(intent.customMaxBudget) : "",
    cities: resolveIntentCities(intent),
    neighborhood: intent.neighborhood || "",
    vehicleType: intent.vehicleType,
    useCases: intent.useCases || [],
    transmission: intent.transmission,
    fuel: intent.fuel,
    financingPreference: intent.financingPreference,
    downPayment: intent.downPayment ? String(intent.downPayment) : "",
    loanTermMonths: intent.loanTermMonths ?? null,
    purchaseTimeline: intent.purchaseTimeline,
    name: intent.name || "",
    whatsapp: intent.whatsapp || "",
    email: intent.email || "",
    consentToShare: Boolean(intent.consentToShare),
  };
}

export function labelForOption(options, value) {
  return options.find((option) => option.value === value)?.label || value || "—";
}

export function formatDownPaymentLabel(downPayment, quizCopy) {
  const num = downPayment != null && downPayment !== "" ? Number(downPayment) : null;
  if (!Number.isFinite(num) || num <= 0) return "—";
  if (typeof quizCopy?.formatCustomBudgetLabel === "function") {
    return quizCopy.formatCustomBudgetLabel(num);
  }
  return String(num);
}

export function formatLoanTermLabel(loanTermMonths, quizCopy) {
  if (!loanTermMonths) return "—";
  const match = quizCopy?.loanTermOptions?.find((option) => option.value === loanTermMonths);
  return match?.label || `${loanTermMonths} mo`;
}

export function financingNeedsLoanTerms(financingPreference) {
  return FINANCING_LOAN_OPTION_VALUES.includes(financingPreference);
}

export function budgetLabel(budgetRange, customMaxBudget, quizCopy) {
  const match = quizCopy?.budgetOptions?.find((option) => option.value === budgetRange);
  if (match) return match.label;
  if (customMaxBudget) {
    const num = Number(customMaxBudget);
    if (Number.isFinite(num) && num > 0 && typeof quizCopy?.formatCustomBudgetLabel === "function") {
      return quizCopy.formatCustomBudgetLabel(num);
    }
  }
  return "—";
}

export function formatUseCaseLabels(useCases, quizCopy) {
  if (!useCases?.length) return "—";
  if (quizCopy?.optionLabel) {
    return useCases.map((value) => quizCopy.optionLabel("useCase", value)).join(", ");
  }
  return useCases.join(", ");
}

export function formatLocationLabel(state) {
  const cities = resolveIntentCities(state);
  const cityPart = cities.length ? cities.join(", ") : "";
  return [cityPart, state.neighborhood?.trim()].filter(Boolean).join(" · ") || "—";
}

const HIGH_INTENT_USE_CASES = new Set(["familia", "trabajo", "montana", "primera"]);

export function calculateLeadScore(state) {
  let score = 0;

  const timeline = TIMELINE_OPTION_VALUES.find((value) => value === state.purchaseTimeline);
  if (state.purchaseTimeline === "today") score += 20;
  else if (state.purchaseTimeline === "this_month") score += 15;

  if (state.whatsapp?.trim()) score += 20;
  if (state.budgetRange || state.customMaxBudget) score += 15;

  const financingClear =
    state.financingPreference && !["unsure"].includes(state.financingPreference);
  if (financingClear) score += 10;
  if (financingNeedsLoanTerms(state.financingPreference) && state.loanTermMonths) score += 5;
  if (state.downPayment) score += 5;

  if (state.vehicleType && state.vehicleType !== "unsure") score += 10;

  if (state.useCases?.some((value) => HIGH_INTENT_USE_CASES.has(value))) {
    score += 15;
  }

  void timeline;
  return Math.min(100, score);
}

export function leadTemperature(score, quizCopy) {
  if (score >= 80) {
    return {
      label: quizCopy?.leadTemperature?.hot || "Alta intención",
      className: "hot",
    };
  }
  if (score >= 60) {
    return {
      label: quizCopy?.leadTemperature?.warm || "Buena oportunidad",
      className: "warm",
    };
  }
  if (score >= 40) {
    return {
      label: quizCopy?.leadTemperature?.mild || "Explorando opciones",
      className: "mild",
    };
  }
  return {
    label: quizCopy?.leadTemperature?.cool || "Investigación inicial",
    className: "cool",
  };
}

export function buildBuyerIntent(state) {
  return {
    budgetRange: state.budgetRange,
    customMaxBudget: state.customMaxBudget ? Number(state.customMaxBudget) : null,
    cities: [],
    city: null,
    neighborhood: null,
    vehicleType: state.vehicleType,
    useCases: state.useCases || [],
    transmission: state.transmission,
    fuel: state.fuel,
    financingPreference: state.financingPreference,
    downPayment: state.downPayment ? Number(state.downPayment) : null,
    loanTermMonths: state.loanTermMonths ? Number(state.loanTermMonths) : null,
    purchaseTimeline: state.purchaseTimeline,
    name: state.name?.trim() || "",
    whatsapp: state.whatsapp?.trim() || "",
    email: state.email?.trim() || null,
    consentToShare: Boolean(state.consentToShare),
    createdAt: new Date().toISOString(),
    source: QUIZ_SOURCE,
  };
}

export function buildBuyerIntentSummary(state, quizCopy) {
  return {
    budget: budgetLabel(state.budgetRange, state.customMaxBudget, quizCopy),
    vehicleType: quizCopy?.optionLabel
      ? quizCopy.optionLabel("vehicleType", state.vehicleType)
      : state.vehicleType || "—",
    useCase: formatUseCaseLabels(state.useCases, quizCopy),
    financing: quizCopy?.optionLabel
      ? quizCopy.optionLabel("financing", state.financingPreference)
      : state.financingPreference || "—",
    downPayment: formatDownPaymentLabel(state.downPayment, quizCopy),
    loanTerm: formatLoanTermLabel(state.loanTermMonths, quizCopy),
    timeline: quizCopy?.optionLabel
      ? quizCopy.optionLabel("timeline", state.purchaseTimeline)
      : state.purchaseTimeline || "—",
    transmission: quizCopy?.optionLabel
      ? quizCopy.optionLabel("transmission", state.transmission)
      : state.transmission || "—",
    fuel: quizCopy?.optionLabel
      ? quizCopy.optionLabel("fuel", state.fuel)
      : state.fuel || "—",
  };
}

export function validateQuizStep(stepIndex, state, validationCopy) {
  const messages = validationCopy || {};
  switch (stepIndex) {
    case 0:
      if (!state.budgetRange && !state.customMaxBudget) {
        return messages.budget || "Selecciona un rango de presupuesto o indica un máximo.";
      }
      return null;
    case 1:
      if (!state.vehicleType) return messages.vehicleType || "Selecciona un tipo de vehículo.";
      return null;
    case 2:
      if (!state.useCases?.length) return messages.useCase || "Selecciona al menos un uso principal.";
      return null;
    case 3:
      if (!state.transmission) return messages.transmission || "Selecciona transmisión.";
      if (!state.fuel) return messages.fuel || "Selecciona combustible.";
      return null;
    case 4:
      if (!state.financingPreference) return messages.financing || "Indica tu preferencia de pago.";
      if (financingNeedsLoanTerms(state.financingPreference) && !state.loanTermMonths) {
        return messages.loanTerm || "Selecciona un plazo de crédito aceptable.";
      }
      return null;
    case 5:
      if (!state.purchaseTimeline) return messages.timeline || "Selecciona tu tiempo de compra.";
      return null;
    case 6:
      if (!state.name?.trim()) return messages.name || "Ingresa tu nombre.";
      if (!state.whatsapp?.trim()) return messages.whatsapp || "Ingresa tu número de WhatsApp.";
      if (!state.consentToShare) {
        return (
          messages.consent ||
          "Debes aceptar compartir tu solicitud con concesionarios verificados."
        );
      }
      return null;
    default:
      return null;
  }
}

export function saveQuizResult(slug, payload) {
  try {
    localStorage.setItem(`${QUIZ_STORAGE_KEY}:${slug}`, JSON.stringify(payload));
  } catch {
    // ignore quota errors
  }
}

export function loadQuizResult(slug) {
  try {
    const raw = localStorage.getItem(`${QUIZ_STORAGE_KEY}:${slug}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function buildLeadMessage(state, summary, score, temperature) {
  return [
    "Concierge quiz completado",
    `Score: ${score}/100 (${temperature.label})`,
    `Presupuesto: ${summary.budget}`,
    `Tipo: ${summary.vehicleType}`,
    `Uso: ${summary.useCase}`,
    `Financiación: ${summary.financing}`,
    summary.downPayment && summary.downPayment !== "—"
      ? `Cuota inicial: ${summary.downPayment}`
      : null,
    summary.loanTerm && summary.loanTerm !== "—" ? `Plazo: ${summary.loanTerm}` : null,
    `Timeline: ${summary.timeline}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function budgetRangeToPreferences(state) {
  const option = BUDGET_OPTION_DEFS.find((entry) => entry.value === state.budgetRange);
  const customMax = state.customMaxBudget ? Number(state.customMaxBudget) : null;
  return {
    budgetMin: option?.priceMin ?? null,
    budgetMax: customMax || option?.priceMax || null,
    preferredContactMethod: "whatsapp",
    timeframe: state.purchaseTimeline || null,
  };
}

/** @deprecated Use buyerRecommendationService.fetchBuyerRecommendations instead. */
export function buildMockRecommendations(state, slug) {
  const intent = buildBuyerIntent(state);
  return rankMockRecommendations(intent, slug).map((item) => ({
    ...item,
    whatsappHref: buildRecommendationWhatsappHref(item, intent.name),
  }));
}

export function trackQuizEvent(track, eventType, metadata = {}) {
  if (typeof track === "function") {
    track(eventType, { component: "buyer_concierge_quiz", ...metadata });
  }
  trackCentralQuizEvent(eventType, {
    quizSessionId: metadata.quizSessionId,
    stepId: metadata.stepId,
    stepIndex: metadata.step ?? metadata.stepIndex,
    ...metadata,
  });
}

// Milestone 9: dealer routing hooks live in buyerRecommendationDealerRouting.js
